import {
  listMenuItemSx,
  sideMenuDrawerSx,
  sideMenuGroupHeaderSx,
  sideMenuListSx,
  sideMenuFooterSx,
  sideMenuCollapseButtonSx,
} from 'components/styles/menu';
import {
  Box, Button, ListItemButton, Drawer, Typography, Divider, List, ListItemText
} from 'components/ui';
import {
  ProbelistMenuHideIcon,
  ProbelistUsersIcon,
  ProbelistDashboardIcon,
  ProbelistInspectionsIcon,
  ProbelistItemsIcon,
  ProbelistObjectsIcon,
  ProbelistProfileIcon,
  ProbelistSettingsIcon,
  ProbelistTemplatesIcon,
  ProbelistPaymentIcon,
  ProbelistCalendarIcon,
  DoDisturbIcon,
} from 'components/uiIcons';
import { useMenu } from 'contexts/menu';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DashboardLink,
  InspectionsLink,
  InspectionsCalendarLink,
  InspectionSubjectsLink,
  PlacesLink,
  ProfileLink,
  SpaceShowLink,
  TemplatesLink,
  SpaceUsersLink,
  SpaceBillingLink,
} from 'utils/link';
import LogoutButton from 'components/layout/logout_button';
import SpaceSelector from 'components/layout/space_selector';
import useMobile from 'hooks/use_mobile';
import useUserHeader from 'hooks/use_user_header';

const SideMenu = () => {
  const collapseButtonRef = useRef(null);
  const location = useLocation();
  const isMobile = useMobile();
  const navigate = useNavigate();
  const { sidebarVisible, setSidebarVisible, sidebarToggleFocusRef } = useMenu();
  const { user, space } = useUserHeader();
  const fullWidth = isMobile && sidebarVisible;

  const onItemClick = (link) => {
    if (isMobile) setSidebarVisible(false);
    navigate(link);
  };

  const isSelected = (link) =>
    location.pathname === link || location.pathname.startsWith(`${link}/`);

  const menuItem = (link, translationId, icon, selected = isSelected(link), enabled = space) => (
    <ListItemButton sx={listMenuItemSx} key={link} onClick={() => onItemClick(link)} selected={selected} aria-current={selected ? 'page' : undefined} disabled={!enabled}>
      {icon}
      <ListItemText>
        <FormattedMessage id={translationId} />
      </ListItemText>
      {!enabled && <DoDisturbIcon />}
    </ListItemButton>
  );

  return (
    <Drawer sx={sideMenuDrawerSx}
      id="side-menu"
      variant="persistent"
      className={fullWidth ? 'full-width' : ''}
      open={sidebarVisible}
      slotProps={{ transition: { onEntered: () => {
        if (!isMobile && sidebarToggleFocusRef.current) {
          collapseButtonRef.current?.focus();
          sidebarToggleFocusRef.current = false;
        }
      } } }}>
      {isMobile && space && <SpaceSelector currentName={space.name} />}
      {isMobile && space && <Divider />}

      <List component="nav" aria-label="Main navigation" sx={sideMenuListSx}>
        <Typography sx={sideMenuGroupHeaderSx}>
          <FormattedMessage id="navbar.group.daily_work" />
        </Typography>
        {menuItem(DashboardLink(), 'navbar.home', <ProbelistDashboardIcon aria-hidden="true" />, isSelected(DashboardLink()), true)}
        {menuItem(
          InspectionsLink(),
          'navbar.inspections',
          <ProbelistInspectionsIcon aria-hidden="true" />,
          isSelected(InspectionsLink()) && !isSelected(InspectionsCalendarLink()),
        )}
        {menuItem(InspectionsCalendarLink(), 'navbar.inspections.calendar', <ProbelistCalendarIcon aria-hidden="true" />)}
        <Typography sx={sideMenuGroupHeaderSx}>
          <FormattedMessage id="navbar.group.configuration" />
        </Typography>
        {menuItem(PlacesLink(), 'navbar.places', <ProbelistObjectsIcon aria-hidden="true" />)}
        {menuItem(InspectionSubjectsLink(), 'navbar.inspection_subjects', <ProbelistItemsIcon aria-hidden="true" />)}
        {user.adminRole && menuItem(TemplatesLink(), 'navbar.templates', <ProbelistTemplatesIcon aria-hidden="true" />)}

        {user.adminRole && (
          <>
            <Typography sx={sideMenuGroupHeaderSx}>
              <FormattedMessage id="navbar.group.space" />
            </Typography>
            {menuItem(SpaceShowLink(), 'navbar.space', <ProbelistSettingsIcon aria-hidden="true" />)}
            {menuItem(SpaceUsersLink(), 'navbar.users', <ProbelistUsersIcon aria-hidden="true" />)}
            {menuItem(SpaceBillingLink(), 'navbar.space.billing', <ProbelistPaymentIcon aria-hidden="true" />)}
          </>
        )}

        {isMobile && (
          <>
            <Typography sx={sideMenuGroupHeaderSx}>
              <FormattedMessage id="navbar.group.account" />
            </Typography>
            {menuItem(ProfileLink(), 'navbar.profile', <ProbelistProfileIcon aria-hidden="true" />, false, true)}
            <LogoutButton />
          </>
        )}
      </List>
      {!isMobile && (
        <Box sx={sideMenuFooterSx}>
          <Button ref={collapseButtonRef} sx={sideMenuCollapseButtonSx}
            aria-expanded={sidebarVisible}
            aria-controls="side-menu"
            onClick={() => {
              sidebarToggleFocusRef.current = true;
              setSidebarVisible(false);
            }}
            startIcon={<ProbelistMenuHideIcon aria-hidden="true" />}>
            <FormattedMessage id="navbar.menu.collapse" />
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default SideMenu;
